import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import hrService from '../services/hrService';

const HRDashboard = () => {
  const { user } = useAuth();
  const [hrStatus, setHRStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && ['HR', 'HRO'].includes(user.role)) {
      fetchHRStatus();
    }
  }, [user]);

  const fetchHRStatus = async () => {
    try {
      setLoading(true);
      const response = await hrService.getHRStatus();
      
      if (response.success) {
        setHRStatus(response.data);
      } else {
        toast.error(response.error || 'Failed to load HR status');
      }
    } catch (error) {
      console.error('Fetch HR status error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to load HR status';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user is HR or HRO
  if (!user || !['HR', 'HRO'].includes(user.role)) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to HR and HRO users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading HR status...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">HR Dashboard</h1>
          <p className="text-gray-600">Manage your HR account and lifecycle</p>
        </div>

        {/* Account Status Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Status</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Role:</span>
                  <span className="font-medium">{hrStatus?.role}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Employee ID:</span>
                  <span className="font-medium">{hrStatus?.employeeId || 'Not assigned'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Place:</span>
                  <span className="font-medium">{hrStatus?.place || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Status:</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    hrStatus?.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {hrStatus?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Joining Letter:</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    hrStatus?.hasJoiningLetter 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {hrStatus?.hasJoiningLetter ? 'Uploaded' : 'Pending'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Joined At:</span>
                  <span className="font-medium">{formatDate(hrStatus?.joinedAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Resignation Status:</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    hrStatus?.isResigned 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {hrStatus?.isResigned ? 'Resigned' : 'Active Employee'}
                  </span>
                </div>
                {hrStatus?.isResigned && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Resigned At:</span>
                    <span className="font-medium">{formatDate(hrStatus?.resignedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {hrStatus?.isResigned && hrStatus?.resignationReason && (
            <div className="mt-6 p-4 bg-red-50 rounded-lg">
              <h3 className="text-sm font-medium text-red-800 mb-2">Resignation Reason:</h3>
              <p className="text-red-700">{hrStatus.resignationReason}</p>
            </div>
          )}
        </div>

        {/* Actions Card */}
        {!hrStatus?.isResigned && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions</h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="text-lg font-medium text-blue-800 mb-2">Joining Letter Management</h3>
                <p className="text-blue-700">
                  Joining letter upload is managed by ADMIN during account creation. Contact ADMIN for any corrections.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default HRDashboard;